import { useTranslation } from 'react-i18next'
import { RiAddLine, RiArrowDownLine, RiArrowUpLine, RiDeleteBinLine } from '@remixicon/react'
import {
  TABLE_COLUMN_TYPES,
  TABLE_LIMITS,
  tableConfigError,
  type QuestionConfig,
  type SurveyQuestion,
  type TableColumn,
  type TableColumnType,
  type TableRow,
} from 'shared'
import { Alert, Button, Field, Input, Select, Textarea } from '../ui'
import { cx, focusRing } from '../../lib/utils'
import { slugKey, withoutColumn } from './tableAnswers'
import TableField from './TableField'

/**
 * Edits the definition of a table question: its columns, what the person
 * answering may do with rows, and the rows the company will start from.
 */
export default function TableBuilder({
  config,
  onChange,
}: {
  config: QuestionConfig
  onChange: (config: QuestionConfig) => void
}) {
  const { t } = useTranslation()
  const columns = config.columns ?? []
  const seedRows = config.seedRows ?? []
  const problem = tableConfigError(config)

  const setColumns = (next: TableColumn[], rows: TableRow[] = seedRows) =>
    onChange({ ...config, columns: next, seedRows: rows })

  const updateColumn = (index: number, patch: Partial<TableColumn>) =>
    setColumns(columns.map((column, i) => (i === index ? { ...column, ...patch } : column)))

  const renameColumn = (index: number, label: string) => {
    // The key is what answers are stored under, so it holds still once a row
    // has been written under it. Until then it follows the name.
    const others = columns.filter((_, i) => i !== index).map((column) => column.key)
    const patch: Partial<TableColumn> = { label }
    if (seedRows.length === 0) patch.key = slugKey(label, others)
    updateColumn(index, patch)
  }

  const changeType = (index: number, type: TableColumnType) => {
    const column = columns[index]
    const fresh: TableColumn = {
      key: column.key,
      label: column.label,
      type,
      required: column.required,
      lockedOnSeed: column.lockedOnSeed,
    }
    if (type === 'single_choice' || type === 'multi_choice') fresh.options = []
    // Values written under the old type would no longer fit the new one.
    const rows = seedRows.map((row) => withoutColumn(row, column.key))
    setColumns(columns.map((c, i) => (i === index ? fresh : c)), rows)
  }

  const removeColumn = (index: number) => {
    const key = columns[index].key
    setColumns(
      columns.filter((_, i) => i !== index),
      seedRows.map((row) => withoutColumn(row, key)),
    )
  }

  const move = (index: number, delta: number) => {
    const target = index + delta
    if (target < 0 || target >= columns.length) return
    const next = [...columns]
    ;[next[index], next[target]] = [next[target], next[index]]
    setColumns(next)
  }

  const addColumn = () =>
    setColumns([
      ...columns,
      { key: slugKey('', columns.map((column) => column.key)), label: '', type: 'short_text' },
    ])

  const authorQuestion: SurveyQuestion = {
    id: 'seed',
    order_index: 0,
    code: null,
    type: 'table',
    label: t('surveys.table.seedRows'),
    description: null,
    required: false,
    config: { columns, seedRows: [], allowNewRows: true, maxRows: config.maxRows },
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {t('surveys.table.columns')}
        </p>
        {columns.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('surveys.table.noColumns')}</p>
        )}
        <ul className="space-y-3">
          {columns.map((column, index) => {
            const choice = column.type === 'single_choice' || column.type === 'multi_choice'
            return (
              <li
                key={index}
                data-column={column.key}
                className="rounded-md border border-gray-200 p-3 dark:border-gray-800"
              >
                <div className="grid gap-3 sm:grid-cols-[1.4fr_1fr_auto]">
                  <Field label={t('surveys.table.columnName')} required>
                    <Input
                      value={column.label}
                      onChange={(event) => renameColumn(index, event.target.value)}
                      maxLength={TABLE_LIMITS.maxLabelLength}
                    />
                  </Field>
                  <Field label={t('surveys.table.columnType')}>
                    <Select
                      value={column.type}
                      onChange={(event) => changeType(index, event.target.value as TableColumnType)}
                      options={TABLE_COLUMN_TYPES.map((type) => ({
                        value: type,
                        label: t(`surveys.type_${type}`),
                      }))}
                    />
                  </Field>
                  <div className="flex items-end gap-1 pb-0.5">
                    <IconButton
                      label={t('surveys.moveUp')}
                      onClick={() => move(index, -1)}
                      disabled={index === 0}
                    >
                      <RiArrowUpLine className="size-4" aria-hidden="true" />
                    </IconButton>
                    <IconButton
                      label={t('surveys.moveDown')}
                      onClick={() => move(index, 1)}
                      disabled={index === columns.length - 1}
                    >
                      <RiArrowDownLine className="size-4" aria-hidden="true" />
                    </IconButton>
                    <IconButton
                      label={t('surveys.table.removeColumn', { name: column.label || column.key })}
                      danger
                      onClick={() => removeColumn(index)}
                      disabled={columns.length === 1}
                    >
                      <RiDeleteBinLine className="size-4" aria-hidden="true" />
                    </IconButton>
                  </div>
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {choice && (
                    <Field label={t('surveys.table.columnOptions')} hint={t('surveys.fields.optionsHint')}>
                      <Textarea
                        rows={3}
                        value={(column.options ?? []).map((option) => option.label).join('\n')}
                        onChange={(event) =>
                          updateColumn(index, {
                            options: event.target.value
                              .split('\n')
                              .map((line) => line.trim())
                              .filter(Boolean)
                              .map((line) => ({ value: line, label: line })),
                          })
                        }
                      />
                    </Field>
                  )}
                  {column.type === 'multi_choice' && (
                    <Field label={t('surveys.table.columnMaxSelect')}>
                      <Input
                        type="number"
                        min={1}
                        value={column.maxSelect ?? ''}
                        onChange={(event) =>
                          updateColumn(index, {
                            maxSelect: event.target.value === '' ? undefined : Number(event.target.value),
                          })
                        }
                      />
                    </Field>
                  )}
                  {column.type === 'number' && (
                    <>
                      <Field label={t('surveys.table.columnMin')}>
                        <Input
                          type="number"
                          value={column.min ?? ''}
                          onChange={(event) =>
                            updateColumn(index, {
                              min: event.target.value === '' ? undefined : Number(event.target.value),
                            })
                          }
                        />
                      </Field>
                      <Field label={t('surveys.table.columnMax')}>
                        <Input
                          type="number"
                          value={column.max ?? ''}
                          onChange={(event) =>
                            updateColumn(index, {
                              max: event.target.value === '' ? undefined : Number(event.target.value),
                            })
                          }
                        />
                      </Field>
                      <Field label={t('surveys.table.columnUnit')}>
                        <Input
                          value={column.unit ?? ''}
                          onChange={(event) => updateColumn(index, { unit: event.target.value || undefined })}
                        />
                      </Field>
                    </>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                  <Check
                    label={t('surveys.table.columnRequired')}
                    checked={column.required === true}
                    onChange={(checked) => updateColumn(index, { required: checked || undefined })}
                  />
                  <Check
                    label={t('surveys.table.columnLocked')}
                    hint={t('surveys.table.columnLockedHint')}
                    checked={column.lockedOnSeed === true}
                    onChange={(checked) => updateColumn(index, { lockedOnSeed: checked || undefined })}
                  />
                </div>
              </li>
            )
          })}
        </ul>
        <Button
          variant="secondary"
          className="mt-3"
          onClick={addColumn}
          disabled={columns.length >= TABLE_LIMITS.maxColumns}
        >
          <RiAddLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
          {t('surveys.table.addColumn')}
        </Button>
      </div>

      <div>
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {t('surveys.table.settings')}
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('surveys.table.allowNewRows')}>
            <Select
              value={config.allowNewRows === false ? 'no' : 'yes'}
              onChange={(event) => onChange({ ...config, allowNewRows: event.target.value === 'yes' })}
              options={[
                { value: 'yes', label: t('surveys.table.yes') },
                { value: 'no', label: t('surveys.table.no') },
              ]}
            />
          </Field>
          <Field label={t('surveys.table.maxRows')} hint={t('surveys.table.maxRowsHint', { cap: TABLE_LIMITS.maxRowsCap })}>
            <Input
              type="number"
              min={1}
              max={TABLE_LIMITS.maxRowsCap}
              placeholder={String(TABLE_LIMITS.defaultMaxRows)}
              value={config.maxRows ?? ''}
              onChange={(event) =>
                onChange({
                  ...config,
                  maxRows: event.target.value === '' ? undefined : Number(event.target.value),
                })
              }
            />
          </Field>
        </div>
      </div>

      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {t('surveys.table.seedRows')}
        </p>
        <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">{t('surveys.table.seedRowsHint')}</p>
        <TableField
          question={authorQuestion}
          value={seedRows}
          mode="author"
          onChange={(rows) => onChange({ ...config, seedRows: rows as TableRow[] })}
        />
      </div>

      {problem && (
        <Alert variant="warning">{t('surveys.table.invalid', { problem })}</Alert>
      )}
    </div>
  )
}

function Check({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string
  hint?: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200" title={hint}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 size-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-900"
      />
      {label}
    </label>
  )
}

function IconButton({
  label,
  onClick,
  disabled,
  danger,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  danger?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={cx(
        'rounded-md p-1.5 text-gray-400 transition disabled:opacity-30',
        danger
          ? 'hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10'
          : 'hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800',
        focusRing,
      )}
    >
      {children}
    </button>
  )
}
